import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import FAQItem from '../common/FAQItem';

const FAQPage = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: "Do I need coding experience?",
            answer: "No. We designed DataHelix for beginners. You start with the basics of SQL and gradually move to complex business logic. No prior programming knowledge is required."
        },
        {
            question: "Who is DataHelix for?",
            answer: "It is for aspiring Data Analysts, Analytics Engineers, and anyone who wants to learn how to answer business questions with data. It’s also great for experienced professionals looking to brush up on advanced SQL patterns."
        },
        {
            question: "How is DataHelix different?",
            answer: "Unlike other platforms that focus on abstract syntax puzzles, DataHelix focuses on Business Case Studies. You solve real problems (e.g., 'Calculate Monthly Retention') using a real database, simulating the actual work of a Data Analyst."
        },
        {
            question: "What skills will I learn?",
            answer: "You will master SQL (PostgreSQL dialect), Data Modeling, Business Logic translation, and Analytical Thinking. We also have modules planned for Python and dbt."
        },
        {
            question: "How long does it take?",
            answer: "The core 'Thinking in Data' curriculum takes about 20-30 hours to complete. However, it is self-paced, so you can learn at your own speed."
        },
        {
            question: "Will I work on real-world projects?",
            answer: "Yes. Every challenge is based on a real-world scenario. You will work with realistic datasets (e-commerce, fintech, SaaS) to solve problems that actual companies face."
        },
        {
            question: "Is DataHelix free?",
            answer: "Yes, DataHelix is currently 100% free for individual learners. Our mission is to democratize data education and bridge the gap between theory and practice."
        },
        {
            question: "What roadmap does DataHelix follow?",
            answer: "We follow the modern data stack roadmap: SQL Fundamentals -> Advanced SQL & Analytics Engineering -> Python for Data Science. We are constantly adding new case studies and modules."
        },
        // --- Additional Questions ---
        {
            question: "Do you offer certificates?",
            answer: "Currently, we prioritize building a provable portfolio over verifiable certificates. Your profile tracks completed case studies, which you can share with recruiters as proof of your skills."
        },
        {
            question: "Is there a community?",
            answer: "Community features are coming soon! We are building a space for learners to discuss SQL problems and career advice."
        },
        {
            question: "Does it work on mobile?",
            answer: "Yes, the platform is responsive. You can read case studies, review logic, and even run queries on your phone or tablet, though a larger screen is recommended for complex SQL."
        },
        {
            question: "How do I report a bug?",
            answer: "You can use the 'Contact Us' page to report any bugs or issues directly to our team. We appreciate your help in improving the platform!"
        },
        {
            question: "Can I contribute a challenge?",
            answer: "Absolutely. You can send your case study ideas directly via the 'Contact Us' page, the feedback widget, or by emailing us. We love community contributions."
        },
        {
            question: "Do you support other SQL dialects?",
            answer: "We focus on PostgreSQL syntax as it is the industry standard for modern data stacks. However, the concepts and logic apply to MySQL, SQL Server, and BigQuery."
        },
        {
            question: "How often is content updated?",
            answer: "We release new challenges and case studies on a regular basis. Check our roadmap or join the community for updates."
        },
        {
            question: "I forgot my password. How do I reset it?",
            answer: "You can reset it via the 'Forgot Password' link on the login page. You'll receive an email with instructions to create a new one."
        },
        {
            question: "Is my progress saved?",
            answer: "Yes, if you are logged in, your progress, solved queries, and completion status are automatically saved to your account."
        },
        {
            question: "Who creates the content?",
            answer: "Content is created by Senior Data Analysts and Analytics Engineers from top tech and finance companies to ensure real-world relevance."
        },
        {
            question: "Can I hire top performers?",
            answer: "If you are an employer looking for talent with proven SQL skills and business acumen, please contact us. We can connect you with our top learners."
        }
    ];

    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    // Structured Data for SEO (FAQPage Schema) - Full List
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "description": "Comprehensive Frequently Asked Questions about DataHelix, SQL training, and Data Analytics.",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <div className="min-h-screen pt-24 pb-12 px-6 bg-white dark:bg-[#0A1220] text-gray-900 dark:text-white transition-colors duration-300">
            <Helmet>
                <title>FAQ | DataHelix</title>
                <meta name="description" content="Frequently asked questions about DataHelix, the interactive SQL learning platform." />
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <span className="text-blue-600 dark:text-blue-400 font-bold uppercase tracking-widest text-sm mb-3 block">
                        Help Center
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Everything you need to know about the platform, curriculum, and mission.
                    </p>
                </div>

                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <FAQItem
                            key={index}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openIndex === index}
                            toggle={() => handleToggle(index)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FAQPage;
